import type { ChangeEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import { POLL_SCORES } from '@/shared/tools/communicationBoardPolls'
import { boardPollsActions } from './boardPollsSagas'
import { selectBoardPollsState } from './boardPollsSelectors'
import { commentChanged, scoreSelected } from './boardPollsSlice'
import { PollCommentOverrides } from './PollForm/styled-components'
import type { BoardPollRoutes } from './types'

export type PollView = 'form' | 'thanks' | 'closed' | 'unavailable'

const POLL_KEY = 'subscriptionForm:communication_board.poll'
const COMMENT_MAX_LENGTH = 1000

interface ViewState {
  voted: boolean
  closed: boolean
  unavailable: boolean
}

const viewOf = ({ voted, closed, unavailable }: ViewState): PollView => {
  if (voted) return 'thanks'
  if (unavailable) return 'unavailable'
  if (closed) return 'closed'

  return 'form'
}

interface UseBoardPollOptions {
  pollKey: string
  question: string
}

const useBoardPoll = ({ pollKey, question }: UseBoardPollOptions) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { drafts, votes, sendingKeys, failedKeys, closedKeys } = useSelector(selectBoardPollsState)
  const { communicationBoardPollVoteUrl }: BoardPollRoutes = useSelector(selectExtraRoutesAuthData)

  const vote = votes[pollKey] ?? null
  const draft = drafts[pollKey] ?? { score: null, comment: '' }
  const sending = sendingKeys.includes(pollKey)
  const failed = failedKeys.includes(pollKey)
  const view = viewOf({
    voted: vote !== null,
    closed: closedKeys.includes(pollKey),
    unavailable: !communicationBoardPollVoteUrl,
  })

  const faces = POLL_SCORES.map(({ score, Icon, colorKey, softColorKey }) => ({
    score,
    Icon,
    colorKey,
    softColorKey,
    label: t(`common:poll.scores.${score}`),
    selected: draft.score === score,
    dimmed: draft.score !== null && draft.score !== score,
    onSelect: () => dispatch(scoreSelected({ pollKey, score })),
  }))

  const votedFace = view === 'thanks' ? POLL_SCORES.find(({ score }) => score === vote) : undefined

  const onCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    dispatch(commentChanged({ pollKey, comment: event.target.value }))

  const onSubmit = () => dispatch({ type: boardPollsActions.SEND_VOTE, pollKey })

  return {
    title: question || t(`${POLL_KEY}.default_question`),
    formHidden: view !== 'form',
    statusHidden: view !== 'closed' && view !== 'unavailable',
    status: t(`${POLL_KEY}.${view === 'unavailable' ? 'relogin' : 'closed'}`),
    form: {
      faces,
      commentId: `poll-comment-${pollKey}`,
      commentLabel: t(`${POLL_KEY}.comment_label`),
      optionalLabel: t(`${POLL_KEY}.optional`),
      commentPlaceholder: t(`${POLL_KEY}.comment_placeholder`),
      comment: draft.comment,
      commentMaxLength: COMMENT_MAX_LENGTH,
      commentOverrides: PollCommentOverrides,
      onCommentChange,
      submitLabel: t(`${POLL_KEY}.${sending ? 'sending' : 'submit'}`),
      submitDisabled: draft.score === null || sending,
      onSubmit,
      footnote: t(`${POLL_KEY}.${failed ? 'error' : 'hint'}`),
      footnoteTone: failed ? ('danger' as const) : ('muted' as const),
    },
    thanks: votedFace
      ? {
          hidden: false as const,
          text: t(`${POLL_KEY}.thanks`),
          face: votedFace,
          faceLabel: t(`common:poll.scores.${votedFace.score}`),
        }
      : { hidden: true as const },
  }
}

export default useBoardPoll
